import { apiClient } from './api'

export type UserMembership = {
  organization_id: string
  organization_name: string
  organization_type: string
  is_default: boolean
  status?: 'active' | 'suspended' | null
  roles: string[]
}

export type UserData = {
  id: string
  email: string
  full_name: string
  is_active: boolean
  is_superuser?: boolean
  email_verified?: boolean
  profile_photo_url?: string | null
  backup_notification_preference?: string
  created_at: string
  updated_at: string
  organization_id: string
  organization?: {
    id: string
    name: string
    type: string
  }
  roles?: string[]
  memberships?: UserMembership[]
}

export type CreateUserRequest = {
  email: string
  full_name: string
  password: string
  is_active: boolean
  organization_id: string
}

export type UpdateUserRequest = {
  full_name?: string
  email?: string
  is_active?: boolean
  password?: string
  backup_notification_preference?: string
}

export type AddUserMembershipRequest = {
  organization_id: string
  role_ids: string[]
  make_default?: boolean
}

export type UserListResponse = {
  users: UserData[]
  total: number
}

export const userApi = {
  // Get all users from current organization (or all orgs if allOrgs=true for superusers)
  getUsers: (skip = 0, limit = 100, allOrgs = false): Promise<UserData[]> => {
    const params = new URLSearchParams({ skip: String(skip), limit: String(limit) })
    if (allOrgs) params.set('all_orgs', 'true')
    return apiClient.get(`/api/v1/users?${params.toString()}`)
  },

  // Get specific user by ID
  getUser: (userId: string): Promise<UserData> => {
    return apiClient.get(`/api/v1/users/${userId}`)
  },

  // Get current user profile
  getCurrentUser: (): Promise<UserData> => {
    return apiClient.get('/api/v1/auth/me')
  },

  // Create new user (superuser only)
  createUser: (userData: CreateUserRequest): Promise<UserData> => {
    return apiClient.post('/api/v1/users', userData)
  },

  // Create user in current organization (org admin)
  createUserInOrg: (userData: {
    email: string
    full_name: string
    password: string
  }): Promise<UserData> => {
    return apiClient.post('/api/v1/users', {
      ...userData,
      is_active: true,
      organization_id: '00000000-0000-0000-0000-000000000000', // backend ignores this for non-superusers
    })
  },

  // Update user profile (own profile)
  updateCurrentUser: (userData: UpdateUserRequest): Promise<UserData> => {
    return apiClient.put('/api/v1/users/me', userData)
  },

  // Update other user (admin functionality - if available)
  updateUser: (userId: string, userData: UpdateUserRequest): Promise<UserData> => {
    return apiClient.put(`/api/v1/users/${userId}`, userData)
  },

  // Associate existing user to another organization
  addMembership: (userId: string, data: AddUserMembershipRequest): Promise<{ message: string }> => {
    return apiClient.post(`/api/v1/users/${userId}/memberships`, data)
  },

  // Suspende ou reativa o vínculo do usuário com uma org (só superadmin; #1122)
  setMembershipStatus: (
    userId: string,
    organizationId: string,
    status: 'active' | 'suspended',
  ): Promise<{ status: 'active' | 'suspended'; is_default: boolean }> => {
    return apiClient.patch(`/api/v1/users/${userId}/memberships/${organizationId}`, { status })
  },

  // Busca global por e-mail ou nome (só superadmin) — para adicionar um usuário a uma org
  searchUsers: (search: string, limit = 20): Promise<UserData[]> => {
    const params = new URLSearchParams({
      all_orgs: 'true',
      search,
      skip: '0',
      limit: String(limit),
    })
    return apiClient.get(`/api/v1/users?${params.toString()}`)
  },

  // Remove existing user association from organization
  removeMembership: (userId: string, organizationId: string): Promise<{ message: string }> => {
    return apiClient.delete(`/api/v1/users/${userId}/memberships/${organizationId}`)
  },

  // Deactivate user. scope=organization (default) suspende apenas o vínculo na org ativa.
  // scope=global desativa a conta globalmente (apenas super admin).
  deactivateUser: (
    userId: string,
    scope: 'organization' | 'global' = 'organization',
  ): Promise<void> => {
    const params = new URLSearchParams({ scope })
    return apiClient.patch(`/api/v1/users/${userId}/deactivate?${params.toString()}`)
  },

  // Activate user. scope=organization (default) reativa apenas o vínculo na org ativa.
  // scope=global reativa a conta globalmente (apenas super admin).
  activateUser: (
    userId: string,
    scope: 'organization' | 'global' = 'organization',
  ): Promise<void> => {
    const params = new URLSearchParams({ scope })
    return apiClient.patch(`/api/v1/users/${userId}/activate?${params.toString()}`)
  },

  // Delete user permanently (only for inactive users)
  deleteUser: (userId: string): Promise<{ message: string }> => {
    return apiClient.delete(`/api/v1/users/${userId}`)
  },

  // Admin: Resend verification email to a user
  resendVerificationEmail: (userId: string, frontendUrl?: string): Promise<{ message: string }> => {
    return apiClient.post(`/api/v1/users/${userId}/resend-verification`, {
      frontend_url: frontendUrl || window.location.origin,
    })
  },

  // Upload profile photo
  uploadProfilePhoto: (file: File): Promise<UserData> => {
    const formData = new FormData()
    formData.append('file', file)
    return apiClient.post('/api/v1/users/me/profile-photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },

  // Delete profile photo
  deleteProfilePhoto: (): Promise<UserData> => {
    return apiClient.delete('/api/v1/users/me/profile-photo')
  },
}

export default userApi
